import { useEffect, useState, useRef } from "react"
import { useEditorPaginas } from "./EditorPaginasContext"
import { FaChevronRight, FaPlus, FaTrash, FaEllipsisH, FaSpinner, FaFileAlt } from "react-icons/fa"

const TreeNode = ({ page, depth = 0 }) => {
  const {
    childrenMap, expandedIds, selectedPageId,
    toggleExpand, ensureExpanded, selectPage, createPage, deletePage,
  } = useEditorPaginas()

  const [menuOpen, setMenuOpen] = useState(false)
  const menuRef = useRef(null)
  const isExpanded = expandedIds.has(page.id)
  const children = childrenMap[page.id] || []
  const isSelected = selectedPageId === page.id
  const hasChildren = children.length > 0 || !childrenMap[page.id]

  useEffect(() => {
    const handler = (e) => {
      if (menuRef.current && !menuRef.current.contains(e.target)) setMenuOpen(false)
    }
    document.addEventListener("mousedown", handler)
    return () => document.removeEventListener("mousedown", handler)
  }, [])

  const handleExpand = (e) => {
    e.stopPropagation()
    toggleExpand(page.id)
  }

  const handleAddChild = (e) => {
    e.stopPropagation()
    setMenuOpen(false)
    createPage({ title: "", parent: page.id, order: children.length })
  }

  const handleDelete = (e) => {
    e.stopPropagation()
    setMenuOpen(false)
    deletePage(page.id)
  }

  return (
    <div>
      <div
        className={`group flex items-center gap-1 py-1 px-1.5 rounded-md cursor-pointer transition-colors ${
          isSelected
            ? "bg-orange-100 text-orange-700"
            : "hover:bg-gray-200 text-gray-700"
        }`}
        style={{ paddingLeft: `${depth * 14 + 4}px` }}
        onClick={() => {
          ensureExpanded(page.id)
          selectPage(page.id)
        }}
      >
        <button
          onClick={handleExpand}
          className={`w-4 h-4 flex items-center justify-center flex-shrink-0 rounded hover:bg-gray-300/50 transition-colors ${
            hasChildren ? "opacity-100" : "opacity-0"
          }`}
        >
          <FaChevronRight
            className={`text-[8px] text-gray-400 transition-transform duration-150 ${
              isExpanded ? "rotate-90" : ""
            }`}
          />
        </button>

        <span className="text-sm flex-shrink-0 leading-none">{page.icon || "📄"}</span>

        <span className="text-xs truncate flex-1 ml-1 select-none">
          {page.title || "Sin título"}
        </span>

        <div
          ref={menuRef}
          className="relative opacity-0 group-hover:opacity-100 flex items-center gap-0.5 flex-shrink-0"
        >
          <button
            onClick={(e) => { e.stopPropagation(); setMenuOpen(v => !v) }}
            className="w-5 h-5 flex items-center justify-center rounded hover:bg-gray-300/60 text-gray-400 hover:text-gray-600"
          >
            <FaEllipsisH className="text-[9px]" />
          </button>
          <button
            onClick={handleAddChild}
            className="w-5 h-5 flex items-center justify-center rounded hover:bg-gray-300/60 text-gray-400 hover:text-gray-600"
          >
            <FaPlus className="text-[9px]" />
          </button>

          {menuOpen && (
            <div className="absolute right-0 top-full mt-1 bg-white border border-gray-200 rounded-lg shadow-lg z-50 py-1 min-w-[120px]">
              <button
                onClick={handleAddChild}
                className="flex items-center gap-2 w-full px-3 py-1.5 text-xs text-gray-700 hover:bg-gray-50"
              >
                <FaPlus className="text-[9px] text-gray-400" /> Sub-página
              </button>
              <hr className="my-1 border-gray-100" />
              <button
                onClick={handleDelete}
                className="flex items-center gap-2 w-full px-3 py-1.5 text-xs text-red-500 hover:bg-red-50"
              >
                <FaTrash className="text-[9px]" /> Eliminar
              </button>
            </div>
          )}
        </div>
      </div>

      {isExpanded && children.map(child => (
        <TreeNode key={child.id} page={child} depth={depth + 1} />
      ))}
    </div>
  )
}

const EditorSidebar = () => {
  const { pages, loadingTree, fetchRootPages, createPage } = useEditorPaginas()

  useEffect(() => {
    fetchRootPages()
  }, [fetchRootPages])

  return (
    <div className="w-full border-r border-gray-200 flex flex-col h-full bg-gray-50/80">
      <div className="flex items-center justify-between px-3 py-2.5 border-b border-gray-200">
        <span className="text-[11px] font-semibold text-gray-400 uppercase tracking-wider">Páginas</span>
        <button
          onClick={() => createPage({ title: "", order: pages.length })}
          className="w-6 h-6 flex items-center justify-center rounded-md hover:bg-gray-200 text-gray-400 hover:text-gray-600 transition-colors"
          title="Nueva página"
        >
          <FaPlus className="text-[10px]" />
        </button>
      </div>

      <div className="flex-1 overflow-y-auto p-1.5 [&::-webkit-scrollbar]:w-1.5 [&::-webkit-scrollbar-thumb]:bg-gray-300 [&::-webkit-scrollbar-thumb]:rounded-full">
        {loadingTree ? (
          <div className="flex items-center justify-center py-8">
            <FaSpinner className="animate-spin text-gray-400" />
          </div>
        ) : pages.length === 0 ? (
          <div className="flex flex-col items-center gap-2 py-8 text-center px-4">
            <FaFileAlt className="text-xl text-gray-300" />
            <p className="text-xs text-gray-400">No hay páginas</p>
            <button
              onClick={() => createPage({ title: "Mi primera página", order: 0 })}
              className="text-xs text-orange-500 hover:text-orange-700 font-medium"
            >
              + Crear página
            </button>
          </div>
        ) : (
          pages.map(page => (
            <TreeNode key={page.id} page={page} depth={0} />
          ))
        )}
      </div>
    </div>
  )
}

export default EditorSidebar
